import React from 'react';
import { useStaticQuery, graphql, Link } from 'gatsby';
import styled from 'styled-components';
import htmlReactParser from 'html-react-parser';
import breakpoints from '@rsc/ui-components/breakpoints';
import PageWrapper from '@rsc/ui-components/page-wrapper/PageWrapper';
import styles from '@utils/styles';
import ArrowIcon from '@components/svgs/arrow-icon.svg';

const Container = styled.div`
  background-color: #fff;
  margin-top: 2.8125rem;
  padding: 50px 0;
`;

const VerticalPageTitleContainer = styled.div`
  position: relative;
`;

const VerticalTitle = styled.h3`
  writing-mode: vertical-rl;
  transform: rotate(-180deg);
  margin: 0;
  position: absolute;
  top: 0;
  left: 0;
  width: auto;
  white-space: nowrap;
  letter-spacing: -0.5px;
  font-size: ${styles.fontSize['3xl']};
  line-height: 38px;
  font-weight: ${styles.fontWeight.medium};
  color: ${styles.colors.blue[100]};
  text-transform: capitalize;
  display: block;
  text-align: center;
`;

const InnerContainer = styled.div`
  padding: 0 40px;
  max-width: ${breakpoints.xl};
  margin: 0 auto;
`;

const ContentContainer = styled.div`
  display: flex;
  padding-left: 2rem;
  flex-direction: column;

  @media (min-width: ${breakpoints.sm}) {
    flex-direction: row;
    flex-wrap: wrap;
  }

  @media (min-width: ${breakpoints.lg}) {
    flex-wrap: nowrap;
  }
`;

const ContentBox = styled.div`
  padding: 1rem 0;
  max-width: 350px; // prevent article body from overrunning image width

  @media (min-width: ${breakpoints.md}) {
    width: 40%;
    padding: 1rem;
  }

  @media (min-width: ${breakpoints.lg}) {
    width: 33.33%;
    padding: 0 25px;
  }
`;

const StyledParagraph = styled.p`
  font-size: ${styles.fontSize.base};
  font-weight: ${styles.fontWeight.light};
  line-height: 24px;
  color: ${styles.colors.gray[700]};

  @media (min-width: ${breakpoints.lg}) {
    min-height: 120px;
  }

  a {
    &:hover {
      color: ${styles.colors.orange[500]};

      svg {
        fill: ${styles.colors.orange[500]};
        stroke: ${styles.colors.orange[500]};
      }
    }
  }
`;

const StyledLink = styled(Link)`
  text-decoration: none;
  font-weight: ${styles.fontWeight.semiBold};
  color: ${styles.colors.gray[700]};
  transition: color 0.3s ease;

  &:hover {
    color: ${styles.colors.orange[500]};
  }
`;

const ImageContainer = styled.div`
  min-height: 205px;
  img {
    width: 100%; // force large images from overflowing
  }
`;

const ReadMoreIcon = styled(ArrowIcon)`
  height: 18px;
  fill: ${styles.colors.gray[700]};
  stroke: ${styles.colors.gray[700]};
  transition: all 0.3s ease;
  vertical-align: middle;
`;

const RecentArticles = () => {
  const data = useStaticQuery(graphql`
    query recentPosts {
      recentArticles: allShopifyBlogPost(
        limit: 3
        sort: { publishedAt: DESC }
      ) {
        nodes {
          title
          handle
          excerpt
          image {
            url
            altText
          }
        }
      }
    }
  `);

  const { recentArticles } = data;

  return (
    <Container>
      <PageWrapper>
        <VerticalPageTitleContainer>
          <VerticalTitle>Recent posts</VerticalTitle>
        </VerticalPageTitleContainer>
      </PageWrapper>
      <InnerContainer>
        <ContentContainer>
          {recentArticles.nodes.map(recentArticle => (
            <ContentBox key={recentArticle.handle}>
              <ImageContainer>
                <Link to={`/blog/${recentArticle.handle}`}>
                  <img
                    src={`${recentArticle?.image?.url}${
                      recentArticle?.image?.url?.includes('?')
                        ? '&height=200'
                        : '?height=200'
                    }`}
                    alt={recentArticle?.image?.altText || recentArticle.title}
                  />
                </Link>
              </ImageContainer>
              <h2>
                <StyledLink to={`/blog/${recentArticle.handle}`}>
                  {recentArticle.title}
                </StyledLink>
              </h2>
              <StyledParagraph>
                {htmlReactParser(recentArticle.excerpt || '')}
              </StyledParagraph>
              <StyledParagraph>
                <Link to={`/blog/${recentArticle.handle}`}>
                  Read More About {recentArticle.title}{' '}
                  <ReadMoreIcon transform="rotate(270)" />
                </Link>
              </StyledParagraph>
            </ContentBox>
          ))}
        </ContentContainer>
      </InnerContainer>
    </Container>
  );
};

export default RecentArticles;
